import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  MapContainer, TileLayer, Marker, useMapEvents,
  Circle, Popup, useMap, Polyline, Rectangle,
} from "react-leaflet";
import {
  Crosshair, Ruler, Hexagon, Calculator, FileDown,
  Layers, Plus, Minus, Home, X, MapPin, Trash2,
  AlertCircle, Check, Sliders, Lock, ShoppingCart,
  Phone, CreditCard, Smartphone, Loader, ChevronRight, ArrowLeft,
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./InteractiveMap.css";
import { getAuthHeaders, fetchWithAuth } from '../../utils/auth';
import { paymentsApi } from '../../api/client';
import { useCart } from '../../contexts/CartContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const URL_DOMAINE = process.env.REACT_APP_API_URL;
const WS_BASE     = process.env.REACT_APP_WS_URL;
export const URLS_BASE    = `${URL_DOMAINE}/api/v1`;
export const WS_URL_BASE  = `${WS_BASE}/geo/laborde-converter/`;

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';

const TILE_LAYERS = {
  dark: {
    label: "Sombre",
    color: "#818cf8",
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ",
    maxNative: 16,
    className: "imap-tiles-dark",
  },
  osm: {
    label: "Standard",
    color: "#10b981",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    maxNative: 19,
  },
  satellite: {
    label: "Satellite",
    color: "#f59e0b",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    maxNative: 17,
  },
};

// ─────────────────────────────────────────────────────────────
// ICONS LEAFLET
// ─────────────────────────────────────────────────────────────
const createIcon = (url, size = [20, 20], cls = "") =>
  new L.Icon({ iconUrl: url, iconSize: size, iconAnchor: [size[0] / 2, size[1] / 2], popupAnchor: [0, -size[1] / 2], className: cls });

const markerIcon  = createIcon("https://cdn-icons-png.flaticon.com/512/684/684908.png", [25, 25]);
const measureIcon = createIcon("https://cdn-icons-png.flaticon.com/512/2850/2850383.png", [14, 14]);

// ─────────────────────────────────────────────────────────────
// ICÔNES GÉODÉSIQUES — cercle coloré
// vert = a une fiche PDF, orange = sans fiche, violet = acheté
// ─────────────────────────────────────────────────────────────
function createGeoCircleIcon(hasPdf, isPurchased, isSelected = false, isHovered = false) {
  let color, borderColor, glowColor;

  if (isPurchased) {
    color       = '#4f46e5';
    borderColor = '#818cf8';
    glowColor   = 'rgba(129,140,248,0.7)';
  } else if (hasPdf) {
    color       = '#16a34a';
    borderColor = '#22c55e';
    glowColor   = 'rgba(34,197,94,0.7)';
  } else {
    color       = '#c2410c';
    borderColor = '#f97316';
    glowColor   = 'rgba(249,115,22,0.7)';
  }

  if (isSelected) {
    borderColor = '#a78bfa';
    glowColor   = 'rgba(167,139,250,0.8)';
  }

  const r   = isHovered ? 9 : isSelected ? 8 : 6;
  const sz  = r * 2 + 8;
  const cx  = sz / 2;
  const sw  = isHovered ? 2.5 : 2;
  const glow = isHovered ? 10 : isSelected ? 7 : 4;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${sz}" height="${sz}" viewBox="0 0 ${sz} ${sz}"
    style="overflow:visible;filter:drop-shadow(0 0 ${glow}px ${glowColor})">
    <circle cx="${cx}" cy="${cx}" r="${r}" fill="${color}" stroke="${borderColor}" stroke-width="${sw}"/>
    ${isSelected ? `<circle cx="${cx}" cy="${cx}" r="${r + 3}" fill="none" stroke="${borderColor}" stroke-width="1" stroke-dasharray="3 2" opacity="0.5"/>` : ''}
  </svg>`;

  return L.divIcon({
    html:        `<div class="geo-icon">${svg}</div>`,
    className:   '',
    iconSize:    [sz, sz],
    iconAnchor:  [cx, cx],
    popupAnchor: [0, -(cx + 6)],
  });
}

// ─────────────────────────────────────────────────────────────
// ICÔNES GÉODÉSIQUES SVG — triangle topographique adaptatif
// (conservé pour usage interne si besoin)
// ─────────────────────────────────────────────────────────────
const GEO_COLORS = {
  dark: {
    normal:   { fill: '#082f49', stroke: '#22d3ee', dot: '#67e8f9', glow: 'rgba(6,182,212,0.7)'   },
    selected: { fill: '#2e1065', stroke: '#a78bfa', dot: '#ede9fe', glow: 'rgba(167,139,250,0.8)' },
    hovered:  { fill: '#431407', stroke: '#fb923c', dot: '#fed7aa', glow: 'rgba(251,146,60,0.8)'  },
  },
  osm: {
    normal:   { fill: '#0c4a6e', stroke: '#0369a1', dot: '#7dd3fc', glow: 'rgba(3,105,161,0.5)'   },
    selected: { fill: '#3b0764', stroke: '#7c3aed', dot: '#ddd6fe', glow: 'rgba(124,58,237,0.55)' },
    hovered:  { fill: '#7c2d12', stroke: '#ea580c', dot: '#fed7aa', glow: 'rgba(234,88,12,0.55)'  },
  },
  satellite: {
    normal:   { fill: '#451a03', stroke: '#fbbf24', dot: '#fef9c3', glow: 'rgba(251,191,36,0.85)' },
    selected: { fill: '#500724', stroke: '#e879f9', dot: '#fae8ff', glow: 'rgba(232,121,249,0.8)' },
    hovered:  { fill: '#1a2e05', stroke: '#a3e635', dot: '#ecfccb', glow: 'rgba(163,230,53,0.8)'  },
  },
};

function createGeodesicIcon(isSelected, isHovered, layer = 'dark') {
  const palette = GEO_COLORS[layer] ?? GEO_COLORS.dark;
  const c   = isHovered ? palette.hovered : isSelected ? palette.selected : palette.normal;
  const sz  = isHovered ? 34 : isSelected ? 28 : 20;
  const sw  = isHovered ? 2.2 : 1.8;
  const cx  = sz / 2;
  const baseY = sz - 5;
  const dotCy = baseY - 7;
  const dotR  = isHovered ? 4 : isSelected ? 3 : 2;
  const pts = `${cx},2 ${sz - 2},${baseY} 2,${baseY}`;

  const selectionRing = isSelected ? `
    <polygon points="${cx},0 ${sz},${sz - 2} 0,${sz - 2}"
      fill="none" stroke="${c.stroke}" stroke-width="0.7"
      stroke-dasharray="3 2" opacity="0.45"/>` : '';

  const stake = isSelected ? `
    <line x1="${cx}" y1="${baseY}" x2="${cx}" y2="${sz - 1}"
      stroke="${c.stroke}" stroke-width="${sw}" stroke-linecap="round"/>` : '';

  const crosshair = isHovered ? `
    <line x1="${cx - 6}" y1="${dotCy}" x2="${cx + 6}" y2="${dotCy}"
      stroke="${c.dot}" stroke-width="1" opacity="0.6"/>
    <line x1="${cx}" y1="${dotCy - 6}" x2="${cx}" y2="${dotCy + 6}"
      stroke="${c.dot}" stroke-width="1" opacity="0.6"/>` : '';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg"
    width="${sz}" height="${sz}" viewBox="0 0 ${sz} ${sz}"
    style="overflow:visible;filter:drop-shadow(0 0 ${isHovered ? 8 : isSelected ? 6 : 3}px ${c.glow})">
    ${selectionRing}
    <polygon points="${pts}"
      fill="${c.fill}" stroke="${c.stroke}"
      stroke-width="${sw}" stroke-linejoin="round"/>
    ${crosshair}
    <circle cx="${cx}" cy="${dotCy}" r="${dotR}" fill="${c.dot}"/>
    ${stake}
  </svg>`;

  return L.divIcon({
    html: `<div class="geo-icon">${svg}</div>`,
    className: '',
    iconSize:    [sz, sz],
    iconAnchor:  [cx, baseY],
    popupAnchor: [0, -(sz)],
  });
}

// ─────────────────────────────────────────────────────────────
// LEAFLET SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────
const ScaleControl = () => {
  const map = useMap();
  useEffect(() => {
    L.control.scale({ imperial: false, metric: true, position: "bottomleft" }).addTo(map);
  }, [map]);
  return null;
};

const MapMover = ({ setMousePosition }) => {
  const lastRef = useRef(null);
  useMapEvents({
    mousemove(e) {
      if (!lastRef.current || Date.now() - lastRef.current > 50) {
        setMousePosition(e.latlng);
        lastRef.current = Date.now();
      }
    },
    mouseout() { setMousePosition(null); },
  });
  return null;
};

const MapClickHandler = ({ onSelect }) => {
  useMapEvents({ click(e) { onSelect(e.latlng); } });
  return null;
};

const MapZoomController = ({ zoomAction, onZoomComplete }) => {
  const map = useMap();
  useEffect(() => {
    if (zoomAction === "IN")  { map.zoomIn(1);  onZoomComplete(); }
    if (zoomAction === "OUT") { map.zoomOut(1); onZoomComplete(); }
  }, [map, zoomAction, onZoomComplete]);
  return null;
};

const MapResetView = ({ trigger, onDone }) => {
  const map = useMap();
  useEffect(() => {
    if (trigger) { map.setView([-18.91552, 47.52166], 15, { animate: true }); onDone(); }
  }, [map, trigger, onDone]);
  return null;
};

const MapMeasureController = ({
  measureMode, measurementPoints, setMeasurementPoints,
  mapSystem, setIsLoading, setMeasureResult, setMeasureError, setMeasureWindowVisible,
}) => {
  const calcWGS84 = useCallback((pts) => {
    if (pts.length < 2) return 0;
    let total = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      total += L.latLng(pts[i].lat, pts[i].lng).distanceTo(L.latLng(pts[i + 1].lat, pts[i + 1].lng));
    }
    return total;
  }, []);

  const calcLaborde = useCallback(async (pts) => {
    setIsLoading(true); setMeasureError(null); setMeasureResult(null);
    if (pts.length < 2) return null;
    try {
      const res = await fetchWithAuth(`${URLS_BASE}/laborde/distance/`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ coordinates: pts.map(p => ({ lat: p.lat, lon: p.lng })) }),
      });
      if (!res.ok) throw new Error("Erreur serveur");
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      return data.total_distance_m;
    } catch (err) {
      setMeasureError("Erreur Laborde : " + err.message);
      return null;
    } finally { setIsLoading(false); }
  }, [setIsLoading, setMeasureError, setMeasureResult]);

  useEffect(() => {
    if (!measureMode || measurementPoints.length < 2) {
      setMeasureResult(null); setMeasureWindowVisible(false); return;
    }
    setMeasureWindowVisible(true);
    if (mapSystem === "WGS84") setMeasureResult(calcWGS84(measurementPoints));
    else calcLaborde(measurementPoints).then(r => { if (r !== null) setMeasureResult(r); });
  }, [measurementPoints, mapSystem, measureMode, calcWGS84, calcLaborde, setMeasureResult, setMeasureWindowVisible]);

  useMapEvents({
    click(e) { if (measureMode) setMeasurementPoints(prev => [...prev, e.latlng]); },
  });

  return (
    <>
      {measurementPoints.length > 1 && (
        <Polyline positions={measurementPoints} color="#06b6d4" weight={2.5} dashArray="6 10" opacity={0.85} />
      )}
      {measurementPoints.map((p, i) => (
        <Marker key={`m${i}`} position={p} icon={measureIcon} opacity={0.9}>
          <Popup><div className="dpopup"><span style={{ color: "#06b6d4", fontWeight: 700 }}>Point {i + 1}</span></div></Popup>
        </Marker>
      ))}
    </>
  );
};

const MapAreaController = ({
  areaMode, areaPoints, setAreaPoints, mapSystem,
  setAreaResult, setAreaError, setGlobalLoading, setAreaWindowVisible,
}) => {
  const calcArea = useCallback(async (pts) => {
    if (pts.length < 3) { setAreaResult(null); return; }
    setGlobalLoading(true); setAreaError(null); setAreaResult(null);
    if (mapSystem === "WGS84") {
      setAreaError("Surface Laborde uniquement disponible en mode LABORDE.");
      setGlobalLoading(false); return;
    }
    try {
      const res = await fetchWithAuth(`${URLS_BASE}/laborde/surface/`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ coordinates: pts.map(p => ({ lat: p.lat, lon: p.lng })) }),
      });
      if (!res.ok) throw new Error("Erreur serveur");
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setAreaResult(data.area_m2);
    } catch (err) { setAreaError("Erreur surface : " + err.message); }
    finally { setGlobalLoading(false); }
  }, [mapSystem, setGlobalLoading, setAreaError, setAreaResult]);

  useEffect(() => {
    if (!areaMode) { setAreaResult(null); setAreaError(null); setAreaWindowVisible(false); return; }
    if (areaPoints.length >= 3) { setAreaWindowVisible(true); calcArea(areaPoints); }
    else if (areaPoints.length > 0) { setAreaWindowVisible(true); setAreaResult(null); setAreaError(null); }
    else { setAreaWindowVisible(false); }
  }, [areaPoints, areaMode, calcArea, setAreaResult, setAreaError, setAreaWindowVisible]);

  useMapEvents({ click(e) { if (areaMode) setAreaPoints(prev => [...prev, e.latlng]); } });

  return (
    <>
      {areaPoints.length >= 2 && (
        <Polyline
          positions={areaPoints.length >= 3 ? [...areaPoints, areaPoints[0]] : areaPoints}
          color="#818cf8" weight={2.5}
          fillColor="rgba(129,140,248,0.12)" fillOpacity={areaPoints.length >= 3 ? 1 : 0}
        />
      )}
      {areaPoints.map((p, i) => (
        <Marker key={`a${i}`} position={p} icon={measureIcon} opacity={0.9}>
          <Popup><div className="dpopup"><span style={{ color: "#818cf8", fontWeight: 700 }}>Sommet {i + 1}</span></div></Popup>
        </Marker>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// DARK PANEL  (drag)
// ─────────────────────────────────────────────────────────────
const DarkPanel = ({ title, icon, children, onClose, initialX = 120, initialY = 80, width = 300 }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: initialX, y: initialY });
  const drag = useRef(false);
  const off  = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onDown = (e) => {
      if (!e.target.closest(".dpanel-header") || e.target.closest("button") || e.target.closest("select")) return;
      e.preventDefault();
      drag.current = true;
      off.current = { x: e.clientX - el.offsetLeft, y: e.clientY - el.offsetTop };
    };
    const onMove = (e) => {
      if (!drag.current) return;
      setPos({ x: e.clientX - off.current.x, y: e.clientY - off.current.y });
    };
    const onUp = () => { drag.current = false; };
    el.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <div ref={ref} className="dpanel" style={{ left: pos.x, top: pos.y, width }}>
      <div className="dpanel-header">
        <div className="dpanel-title">
          {icon && <span className="dpanel-title-icon">{icon}</span>}
          <span>{title}</span>
        </div>
        <button className="dpanel-close" onClick={onClose}><X size={13} /></button>
      </div>
      <div className="dpanel-body">{children}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// PAYMENT MODAL — overlay fixe, design identique au Panier
// ─────────────────────────────────────────────────────────────
const PAY_METHODS = [
  { id: 'stripe', label: 'Carte bancaire', sub: 'Visa / Mastercard',  icon: CreditCard,  color: '#818cf8' },
  { id: 'mvola',  label: 'MVola',          sub: 'Telma Madagascar',    icon: Phone,       color: '#e11d48' },
  { id: 'orange', label: 'Orange Money',   sub: 'Orange Madagascar',   icon: Smartphone,  color: '#f97316' },
  { id: 'airtel', label: 'Airtel Money',   sub: 'Airtel Madagascar',   icon: Smartphone,  color: '#dc2626' },
];

/* ── Petit flip-card décoratif — toujours dark (c'est une carte bancaire) ── */
const MiniCardVisual = ({ amountMga, amountEur }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <div>
      <style>{`
        .mcv-scene{width:100%;height:120px;perspective:900px;cursor:pointer;}
        .mcv-3d{width:100%;height:100%;position:relative;transform-style:preserve-3d;transition:transform .5s cubic-bezier(.4,0,.2,1);}
        .mcv-3d.mcv-fl{transform:rotateY(180deg);}
        .mcv-face{position:absolute;inset:0;border-radius:10px;backface-visibility:hidden;-webkit-backface-visibility:hidden;padding:12px 16px;box-sizing:border-box;display:flex;flex-direction:column;justify-content:space-between;}
        .mcv-front{background:linear-gradient(135deg,#1e1b4b 0%,#312e81 55%,#4338ca 100%);border:1px solid rgba(99,102,241,.45);box-shadow:0 8px 28px rgba(79,70,229,.3);}
        .mcv-back{background:linear-gradient(135deg,#1e1b4b,#1e293b);border:1px solid rgba(99,102,241,.3);transform:rotateY(180deg);}
        .mcv-chip{width:26px;height:20px;border-radius:3px;background:linear-gradient(135deg,#d4a840,#f5c842,#b8922c);}
        .mcv-mag{height:26px;background:#0a0f1e;margin:0 -16px;}
        .mcv-num{letter-spacing:2px;font-size:12px;color:rgba(255,255,255,.9);font-weight:600;font-family:'Courier New',monospace;}
        .mcv-lbl{font-size:8px;color:rgba(255,255,255,.4);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:1px;}
        .mcv-val{font-size:10px;color:rgba(255,255,255,.85);font-family:'Courier New',monospace;}
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <div className="mcv-scene" onClick={() => setFlipped(f => !f)} title="Voir le CVC">
        <div className={`mcv-3d${flipped ? ' mcv-fl' : ''}`}>
          <div className="mcv-face mcv-front">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div className="mcv-chip"/>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:8, color:'rgba(255,255,255,.3)', letterSpacing:1 }}>TEST</div>
                <div style={{ fontSize:9, color:'rgba(165,180,252,.8)', fontWeight:700 }}>STRIPE</div>
              </div>
            </div>
            <div className="mcv-num">4242  4242  4242  4242</div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
              <div><div className="mcv-lbl">Titulaire</div><div className="mcv-val">MADJEO TEST</div></div>
              <div><div className="mcv-lbl">Expire</div><div className="mcv-val">12/34</div></div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:11, color:'#86efac', fontWeight:700, fontFamily:'system-ui' }}>{amountMga} MGA</div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,.4)' }}>{amountEur} EUR</div>
              </div>
            </div>
          </div>
          <div className="mcv-face mcv-back">
            <div className="mcv-mag"/>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ flex:1, height:22, background:'repeating-linear-gradient(90deg,#f1f5f9 0,#f1f5f9 16px,#e2e8f0 16px,#e2e8f0 20px)', borderRadius:2 }}/>
              <div style={{ background:'#fff', border:'1px solid #cbd5e1', borderRadius:3, padding:'3px 7px', fontSize:12, fontWeight:700, color:'#1e293b', letterSpacing:2 }}>123</div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
              <Lock size={8} style={{ color:'#4f46e5' }}/>
              <span style={{ fontSize:8, color:'rgba(255,255,255,.4)' }}>256-bit SSL · Stripe Inc.</span>
            </div>
          </div>
        </div>
      </div>
      <div className="pm-sep-label" style={{ textAlign:'center', marginTop:4 }}>↩ Cliquer pour voir le CVC</div>
    </div>
  );
};

/* ── Formulaire Stripe (thème Stripe toujours dark, enveloppe thème-aware) ── */
const ModalStripeForm = ({ amountEur, amountMga, onPaymentMethod }) => {
  const mountRef    = useRef(null);
  const stripeRef   = useRef(null);
  const elementsRef = useRef(null);
  const [ready,   setReady]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [copied,  setCopied]  = useState(false);

  useEffect(() => {
    if (!mountRef.current) return;
    if (!window.Stripe || !STRIPE_PUBLIC_KEY) { setError('Clé Stripe manquante'); return; }
    const stripe   = window.Stripe(STRIPE_PUBLIC_KEY);
    const elements = stripe.elements({
      mode: 'payment',
      paymentMethodCreation: 'manual',
      amount:   Math.max(Math.round((amountEur || 0.5) * 100), 50),
      currency: 'eur',
      locale:   'fr',
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary:         '#6366f1',
          colorBackground:      '#0f172a',
          colorText:            '#e2e8f0',
          colorTextSecondary:   '#94a3b8',
          colorTextPlaceholder: '#475569',
          colorDanger:          '#f87171',
          colorInputBackground: '#1e293b',
          colorInputText:       '#f1f5f9',
          colorInputBorder:     '#334155',
          fontFamily:           'system-ui,-apple-system,sans-serif',
          fontSizeBase:         '13px',
          borderRadius:         '8px',
        },
        rules: {
          '.Input':         { border:'1px solid #334155', boxShadow:'none' },
          '.Input:focus':   { borderColor:'#6366f1', boxShadow:'0 0 0 3px rgba(99,102,241,0.2)' },
          '.Label':         { color:'#94a3b8', fontSize:'11px', fontWeight:'600', letterSpacing:'0.06em', textTransform:'uppercase' },
          '.Tab':           { border:'1px solid #1e293b', background:'#0f1829', color:'#64748b' },
          '.Tab--selected': { border:'1px solid #6366f1', background:'rgba(99,102,241,0.08)', color:'#818cf8' },
          '.Error':         { color:'#f87171', fontSize:'12px' },
        },
      },
    });
    stripeRef.current   = stripe;
    elementsRef.current = elements;
    const payEl = elements.create('payment', {
      layout:  { type:'tabs', defaultCollapsed:false },
      wallets: { applePay:'never', googlePay:'never' },
    });
    payEl.mount(mountRef.current);
    payEl.on('ready',  () => setReady(true));
    payEl.on('change', e => { if (e.complete) setError(null); });
    return () => { try { payEl.unmount(); } catch {} };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!stripeRef.current || !elementsRef.current) return;
    setLoading(true); setError(null);
    try {
      const { error: e1 } = await elementsRef.current.submit();
      if (e1) { setError(e1.message); return; }
      const { error: e2, paymentMethod } = await stripeRef.current.createPaymentMethod({ elements: elementsRef.current });
      if (e2) { setError(e2.message); return; }
      onPaymentMethod(stripeRef.current, paymentMethod);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCard = () => {
    navigator.clipboard?.writeText('4242424242424242').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      <MiniCardVisual amountMga={amountMga} amountEur={amountEur} />

      {/* Chip test */}
      <div className="pm-test-chip">
        <span className="pm-test-badge">TEST</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div className="pm-test-num">4242 4242 4242 4242</div>
          <div className="pm-test-detail">exp <strong>12/34</strong> · cvc <strong>123</strong></div>
        </div>
        <button onClick={copyCard} style={{
          background: copied ? 'rgba(34,197,94,0.12)' : 'rgba(var(--ds-indigo-rgb),0.12)',
          border:`1px solid ${copied ? 'rgba(34,197,94,0.3)' : 'rgba(var(--ds-indigo-rgb),0.3)'}`,
          borderRadius:5, padding:'3px 8px', cursor:'pointer',
          fontSize:10, color: copied ? '#86efac' : 'var(--cyan-500)', fontFamily:'inherit', flexShrink:0,
        }}>
          {copied ? '✓ Copié' : 'Copier'}
        </button>
      </div>

      <div className="pm-sep">
        <div className="pm-sep-line"/><span className="pm-sep-label">COORDONNÉES BANCAIRES</span><div className="pm-sep-line"/>
      </div>

      {error && <div className="pm-error"><AlertCircle size={12}/>{error}</div>}

      <div className="pm-stripe-mount">
        {!ready && (
          <div className="pm-stripe-loading">
            <Loader size={13} style={{ animation:'spin 0.65s linear infinite' }}/> Chargement…
          </div>
        )}
        <div ref={mountRef} style={{ minHeight:160 }}/>
      </div>

      {ready && (
        <button className="pm-pay-btn" onClick={handleSubmit} disabled={loading}>
          {loading
            ? <><Loader size={13} style={{ animation:'spin 0.65s linear infinite' }}/> Validation…</>
            : <><Lock size={12}/> Payer {amountMga} MGA · {amountEur?.toFixed(2)} EUR</>
          }
        </button>
      )}

      <div className="pm-ssl-note">
        <Lock size={9} style={{ color:'var(--c-dim)' }}/>
        <span>Paiement sécurisé SSL · Stripe · Aucune donnée stockée</span>
      </div>
    </div>
  );
};

const PaymentModal = ({ point, price, onClose, onSuccess }) => {
  const [step,       setStep]      = useState('choose');
  const [method,     setMethod]    = useState(null);
  const [phone,      setPhone]     = useState('');
  const [error,      setError]     = useState(null);
  const [loading,    setLoading]   = useState(false);
  const [purchaseId, setPurchaseId]= useState(null);
  const [procMsg,    setProcMsg]   = useState('');

  const hasPdf    = point?.properties?.has_pdf;
  const amountMga = price?.amount_mga ?? '—';
  const amountEur = price?.amount_eur ?? 0;

  const pollStatus = useCallback(async (pid, pMethod) => {
    const INTERVAL = 4000;
    const MAX_MS   = 180_000;
    const start    = Date.now();
    const fetchFn  = () => {
      if (pMethod === 'mvola')  return paymentsApi.mvolaStatus(pid);
      if (pMethod === 'airtel') return paymentsApi.airtelStatus(pid);
      if (pMethod === 'orange') return paymentsApi.orangeStatus(pid, `MADJEO-${pid}`);
      return Promise.reject(new Error('Méthode inconnue'));
    };
    while (Date.now() - start < MAX_MS) {
      await new Promise(r => setTimeout(r, INTERVAL));
      try {
        const res = await fetchFn();
        const s   = res.data.status;
        if (s === 'completed') return { ok: true };
        if (s === 'failed')    return { ok: false, err: 'Paiement refusé' };
      } catch { /* on réessaie */ }
    }
    return { ok: false, err: 'Délai dépassé (3 min)' };
  }, []);

  const initPurchase = async (m) => {
    setError(null); setLoading(true); setMethod(m);
    try {
      const r = await paymentsApi.initiate({ point_id: point.id, payment_method: m });
      setPurchaseId(r.data.purchase_id);
      setStep(m === 'stripe' ? 'stripe' : m === 'orange' ? 'orange' : 'phone');
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur initialisation');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePaymentMethod = async (stripe, pm) => {
    setStep('processing'); setProcMsg('Confirmation Stripe…');
    try {
      const ir = await paymentsApi.stripeIntent({ purchase_id: purchaseId });
      const { error: ce } = await stripe.confirmCardPayment(ir.data.client_secret, { payment_method: pm.id });
      if (ce) throw new Error(ce.message);
      await paymentsApi.stripeConfirm(purchaseId);
      setStep('success'); onSuccess(point.id);
    } catch (e) {
      setError(e.message || 'Erreur paiement Stripe'); setStep('stripe');
    }
  };

  const handleMobileConfirm = async () => {
    if (!phone.trim()) { setError('Numéro de téléphone requis'); return; }
    setLoading(true); setError(null);
    try {
      if (method === 'mvola') await paymentsApi.mvolaInitiate(purchaseId, phone.trim());
      else                    await paymentsApi.airtelInitiate(purchaseId, phone.trim());
      setProcMsg(`Acceptez le push USSD sur ${phone.trim()} …`);
      setStep('processing');
      const res = await pollStatus(purchaseId, method);
      if (res.ok) { setStep('success'); onSuccess(point.id); }
      else { setError(res.err); setStep('phone'); }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Erreur paiement'); setStep('phone');
    } finally { setLoading(false); }
  };

  const handleOrangeConfirm = async () => {
    setLoading(true); setError(null);
    try {
      const r = await paymentsApi.orangeInitiate(purchaseId);
      if (!r.data.test_mode && r.data.payment_url) window.open(r.data.payment_url, '_blank', 'noopener,noreferrer');
      setProcMsg(r.data.test_mode ? 'Mode test — simulation paiement Orange Money…' : 'Page Orange Money ouverte — finalisez le paiement…');
      setStep('processing');
      const res = await pollStatus(purchaseId, 'orange');
      if (res.ok) { setStep('success'); onSuccess(point.id); }
      else { setError(res.err); setStep('orange'); }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Erreur Orange Money'); setStep('orange');
    } finally { setLoading(false); }
  };

  return (
    <div className="pm-overlay">
      <div className="pm-panel">

        {/* ── En-tête ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {step !== 'choose' && step !== 'success' && step !== 'processing' && (
              <button className="pm-back" onClick={() => { setError(null); setStep('choose'); }}>
                <ArrowLeft size={14}/>
              </button>
            )}
            <div>
              <div className="pm-title">{step === 'success' ? 'Accès déverrouillé !' : 'Accéder aux informations'}</div>
              <div className="pm-sub">{point?.properties?.nom ?? 'Point géodésique'}</div>
            </div>
          </div>
          {step !== 'processing' && (
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--c-muted)', padding:4, flexShrink:0 }}>
              <X size={17}/>
            </button>
          )}
        </div>

        {/* ── Récap prix ── */}
        {step !== 'success' && step !== 'processing' && (
          <div className="pm-recap">
            <div>
              <div className="pm-recap-label">Coordonnées Laborde</div>
              {hasPdf && <div className="pm-recap-pdf">+ Téléchargement PDF</div>}
            </div>
            <div style={{ textAlign:'right' }}>
              <div className="pm-recap-amount">{amountMga} MGA</div>
              <div className="pm-recap-eur">{Number(amountEur).toFixed(2)} EUR</div>
            </div>
          </div>
        )}

        {/* ── Erreur ── */}
        {error && <div className="pm-error"><AlertCircle size={13}/>{error}</div>}

        {/* ══ ÉTAPE : choix ══ */}
        {step === 'choose' && (
          <div>
            <div className="pm-section-label">Mode de paiement :</div>
            {PAY_METHODS.map(m => {
              const Icon = m.icon;
              return (
                <button key={m.id} className="pm-method" onClick={() => initPurchase(m.id)} disabled={loading}>
                  <div style={{ width:32, height:32, borderRadius:7, flexShrink:0, background:`${m.color}15`, border:`1px solid ${m.color}35`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Icon size={14} style={{ color:m.color }}/>
                  </div>
                  <div style={{ flex:1, textAlign:'left' }}>
                    <div className="pm-method-name">{m.label}</div>
                    <div className="pm-method-sub">{m.sub}</div>
                  </div>
                  <span className="pm-method-badge" style={{ color: m.id === 'mvola' ? '#e11d48' : 'var(--c-dim)' }}>
                    {m.id === 'stripe' ? 'Visa / MC' : m.id === 'mvola' ? 'test ✓' : 'test'}
                  </span>
                  <ChevronRight size={13} className="pm-method-chevron"/>
                </button>
              );
            })}
            {loading && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:7, color:'var(--c-muted)', fontSize:12, marginTop:8 }}>
                <Loader size={13} style={{ animation:'spin 0.65s linear infinite' }}/> Initialisation…
              </div>
            )}
          </div>
        )}

        {/* ══ ÉTAPE : Stripe ══ */}
        {step === 'stripe' && purchaseId && (
          <ModalStripeForm amountEur={Number(amountEur)} amountMga={amountMga} onPaymentMethod={handleStripePaymentMethod}/>
        )}

        {/* ══ ÉTAPE : Téléphone (MVola / Airtel) ══ */}
        {step === 'phone' && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div className="pm-phone-label">Numéro {PAY_METHODS.find(m => m.id === method)?.label} :</div>
            <input
              className="dp-input"
              type="tel"
              placeholder={method === 'airtel' ? '032 XX XXX XX' : '034 XX XXX XX'}
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleMobileConfirm()}
            />
            <div className="pm-phone-note">Mode test — tout numéro est accepté.</div>
            <button className="pm-pay-btn" onClick={handleMobileConfirm} disabled={!phone.trim() || loading}>
              {loading
                ? <><Loader size={13} style={{ animation:'spin 0.65s linear infinite' }}/> Envoi…</>
                : <>Payer {amountMga} MGA <ChevronRight size={13}/></>
              }
            </button>
          </div>
        )}

        {/* ══ ÉTAPE : Orange Money ══ */}
        {step === 'orange' && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div className="pm-orange-info">
              Orange Money WebPay — vous serez redirigé vers la page de paiement Orange.<br/>
              <span className="pm-orange-note">Mode test : simulation 8 secondes.</span>
            </div>
            <button className="pm-pay-btn pm-pay-btn--orange" onClick={handleOrangeConfirm} disabled={loading}>
              {loading
                ? <><Loader size={13} style={{ animation:'spin 0.65s linear infinite' }}/> Initialisation…</>
                : <>Payer {amountMga} MGA via Orange Money <ChevronRight size={13}/></>
              }
            </button>
          </div>
        )}

        {/* ══ ÉTAPE : processing ══ */}
        {step === 'processing' && (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div className="pm-proc-icon">
              <Loader size={22} style={{ color:'var(--cyan-500)', animation:'spin 0.8s linear infinite' }}/>
            </div>
            <div className="pm-proc-title">Paiement en cours…</div>
            <div className="pm-proc-msg">{procMsg}</div>
            <div className="pm-proc-note">Ne fermez pas cette fenêtre</div>
          </div>
        )}

        {/* ══ ÉTAPE : succès ══ */}
        {step === 'success' && (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div className="pm-ok-icon"><Check size={26} style={{ color:'#22c55e' }}/></div>
            <div className="pm-ok-title">Accès déverrouillé !</div>
            <div className="pm-ok-sub">
              Coordonnées Laborde disponibles.{hasPdf ? ' La fiche PDF est téléchargeable.' : ''}
            </div>
            <button className="pm-pay-btn" onClick={onClose}>Voir les coordonnées</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PANELS OUTILS
// ─────────────────────────────────────────────────────────────
const GeoSearchPanel = ({ selectedPoint, setSelectedPoint, radius, setRadius, onSubmit, onPickClick, onClose, isLoading, error }) => (
  <DarkPanel title="Points géodésiques" icon={<Crosshair size={14} />} onClose={onClose} initialX={20} initialY={80} width={300}>
    <div className="dp-field">
      <label className="dp-label">Centre de recherche</label>
      <div className="dp-row-3">
        <input className="dp-input" type="number" placeholder="Latitude" value={selectedPoint?.lat ?? ""}
          onChange={e => setSelectedPoint(p => ({ ...(p || {}), lat: e.target.value !== "" ? parseFloat(e.target.value) : "" }))} />
        <input className="dp-input" type="number" placeholder="Longitude" value={selectedPoint?.lng ?? ""}
          onChange={e => setSelectedPoint(p => ({ ...(p || {}), lng: e.target.value !== "" ? parseFloat(e.target.value) : "" }))} />
        <button className="dp-btn dp-btn--icon" onClick={onPickClick} title="Pointer sur la carte"><MapPin size={14} /></button>
      </div>
    </div>
    <div className="dp-field">
      <label className="dp-label">Rayon (km)</label>
      <select className="dp-select" value={radius} onChange={e => setRadius(parseFloat(e.target.value))}>
        {[5,10,20,30,50,100,200,500].map(r => <option key={r} value={r}>{r} km</option>)}
      </select>
    </div>

    {/* Légende couleurs */}
    <div style={{ display: 'flex', gap: 12, marginBottom: 10, padding: '6px 0', borderTop: '1px solid #1e293b', marginTop: 4 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
        Avec fiche
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#c2410c', display: 'inline-block' }} />
        Sans fiche
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#4f46e5', display: 'inline-block' }} />
        Acheté
      </span>
    </div>

    {error && <div className="dp-alert dp-alert--error"><AlertCircle size={13} />{error}</div>}
    <div className="dp-btn-row">
      {isLoading && <span className="dp-spin" style={{width:14,height:14,border:'2px solid rgba(129,140,248,0.18)',borderTopColor:'#818cf8',borderRadius:'50%',animation:'spin 0.65s linear infinite',display:'inline-block'}} />}
      <button className="dp-btn dp-btn--primary" onClick={onSubmit} disabled={isLoading || !selectedPoint || !radius}>
        <Crosshair size={13} /> Rechercher
      </button>
    </div>
  </DarkPanel>
);

const MeasurePanel = ({ onClose, totalDistanceM, mapSystem, isLoading, error, onClear, numPoints }) => {
  const [unit, setUnit] = useState("km");
  const display = useMemo(() => {
    if (totalDistanceM === null || totalDistanceM === 0) return null;
    return unit === "m" ? totalDistanceM.toFixed(2) : (totalDistanceM / 1000).toFixed(3);
  }, [totalDistanceM, unit]);

  return (
    <DarkPanel title={`Distance (${mapSystem})`} icon={<Ruler size={14} />} onClose={onClose} initialX={20} initialY={160} width={290}>
      <div className="dp-metric">
        <div>
          <div className="dp-metric-label">Distance totale</div>
          <div className="dp-info">{numPoints} point(s) — min. 2</div>
        </div>
        {isLoading
          ? <div className="dp-spin" style={{width:20,height:20,border:'2px solid rgba(6,182,212,0.18)',borderTopColor:'#06b6d4',borderRadius:'50%',animation:'spin 0.65s linear infinite'}} />
          : <div className={`dp-metric-value ${numPoints >= 2 && display ? "dp-metric-value--cyan" : "dp-metric-value--muted"}`}>
              {numPoints >= 2 && display ? `${display} ${unit}` : "—"}
            </div>
        }
      </div>
      {error && <div className="dp-alert dp-alert--error"><AlertCircle size={13} />{error}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
        <select className="dp-select" style={{ width: "auto" }} value={unit} onChange={e => setUnit(e.target.value)}>
          <option value="km">Kilomètres (km)</option>
          <option value="m">Mètres (m)</option>
        </select>
        <button className="dp-btn dp-btn--danger" onClick={onClear} disabled={numPoints === 0}>
          <Trash2 size={12} /> Vider
        </button>
      </div>
    </DarkPanel>
  );
};

const AreaPanel = ({ onClose, areaM2, mapSystem, isLoading, error, onClear, numPoints }) => {
  const [unit, setUnit] = useState("km2");
  const display = useMemo(() => {
    if (areaM2 === null || areaM2 === 0) return null;
    return unit === "m2" ? areaM2.toFixed(2) : (areaM2 / 1_000_000).toFixed(4);
  }, [areaM2, unit]);
  const unitLabel = unit === "km2" ? "km²" : "m²";

  return (
    <DarkPanel title={`Surface (${mapSystem})`} icon={<Hexagon size={14} />} onClose={onClose} initialX={20} initialY={160} width={290}>
      <div className="dp-metric">
        <div>
          <div className="dp-metric-label">Surface totale</div>
          <div className="dp-info">{numPoints} sommet(s) — min. 3</div>
        </div>
        {isLoading
          ? <div className="dp-spin" style={{width:20,height:20,border:'2px solid rgba(129,140,248,0.18)',borderTopColor:'#818cf8',borderRadius:'50%',animation:'spin 0.65s linear infinite'}} />
          : <div className={`dp-metric-value ${numPoints >= 3 && display ? "" : "dp-metric-value--muted"}`} style={numPoints >= 3 && display ? {color:"#818cf8"} : {}}>
              {numPoints >= 3 && display ? `${display} ${unitLabel}` : "—"}
            </div>
        }
      </div>
      {error && <div className="dp-alert dp-alert--error"><AlertCircle size={13} />{error}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
        <select className="dp-select" style={{ width: "auto" }} value={unit} onChange={e => setUnit(e.target.value)}>
          <option value="km2">Kilomètres carrés (km²)</option>
          <option value="m2">Mètres carrés (m²)</option>
        </select>
        <button className="dp-btn dp-btn--danger" onClick={onClear} disabled={numPoints === 0}>
          <Trash2 size={12} /> Vider
        </button>
      </div>
    </DarkPanel>
  );
};

const DxfZonePanel = ({ selectedPointDxf, setSelectedPointDxf, sideSquare, setSideSquare, onSubmit, onPickClick, onClose, isLoading, error }) => (
  <DarkPanel title="Zone d'export DXF" icon={<FileDown size={14} />} onClose={onClose} initialX={20} initialY={80} width={300}>
    <div className="dp-field">
      <label className="dp-label">Centre de la zone</label>
      <div className="dp-row-3">
        <input className="dp-input" type="number" placeholder="Latitude" value={selectedPointDxf?.lat ?? ""}
          onChange={e => setSelectedPointDxf(p => ({ ...(p||{}), lat: e.target.value !== "" ? parseFloat(e.target.value) : "" }))} />
        <input className="dp-input" type="number" placeholder="Longitude" value={selectedPointDxf?.lng ?? ""}
          onChange={e => setSelectedPointDxf(p => ({ ...(p||{}), lng: e.target.value !== "" ? parseFloat(e.target.value) : "" }))} />
        <button className="dp-btn dp-btn--icon" onClick={onPickClick} title="Pointer sur la carte"><MapPin size={14} /></button>
      </div>
    </div>
    <div className="dp-field">
      <label className="dp-label">Côté de la zone (m)</label>
      <select className="dp-select" value={sideSquare} onChange={e => setSideSquare(parseFloat(e.target.value))}>
        {[50,100,200,500].map(r => <option key={r} value={r}>{r} m</option>)}
      </select>
    </div>
    {error && <div className="dp-alert dp-alert--error"><AlertCircle size={13} />{error}</div>}
    <div className="dp-btn-row">
      {isLoading && <div style={{width:13,height:13,border:'2px solid rgba(129,140,248,0.18)',borderTopColor:'#818cf8',borderRadius:'50%',animation:'spin 0.65s linear infinite'}} />}
      <button className="dp-btn dp-btn--primary" onClick={onSubmit} disabled={isLoading || !selectedPointDxf}>
        <Check size={13} /> Valider la zone
      </button>
    </div>
  </DarkPanel>
);

const DxfConfigPanel = ({ onClose, bbox, onExport }) => {
  const [coordSystem, setCoordSystem] = useState("WGS84");
  const [elements, setElements] = useState({ batiment: true, route: true, parcelle: false, autreElement: false });

  const toggle = (name) => setElements(p => ({ ...p, [name]: !p[name] }));
  const handleExport = () => onExport({ systeme: coordSystem, elements, bbox });

  return (
    <DarkPanel title="Configuration Export" icon={<FileDown size={14} />} onClose={onClose} initialX={220} initialY={80} width={280}>
      <div className="dp-field">
        <label className="dp-label">Système de coordonnées</label>
        <div className="dp-toggle">
          <button className={`dp-toggle-btn${coordSystem === "WGS84" ? " dp-toggle-btn--active" : ""}`} onClick={() => setCoordSystem("WGS84")}>WGS 84</button>
          <button className={`dp-toggle-btn${coordSystem === "Laborde" ? " dp-toggle-btn--active" : ""}`} onClick={() => setCoordSystem("Laborde")}>LABORDE</button>
        </div>
      </div>
      <div className="dp-sep" />
      <div className="dp-field">
        <label className="dp-label">Éléments à exporter</label>
        {[
          { key: "batiment", label: "Bâtiments", disabled: false },
          { key: "route",    label: "Routes",    disabled: false },
          { key: "parcelle", label: "Parcelles (bientôt)", disabled: true },
          { key: "autreElement", label: "Autres éléments (bientôt)", disabled: true },
        ].map(({ key, label, disabled }) => (
          <label key={key} className="dp-check-row" style={disabled ? { opacity: 0.4, cursor: "default" } : {}}>
            <input type="checkbox" checked={elements[key]} onChange={() => !disabled && toggle(key)} disabled={disabled} />
            <span className="dp-check-label">{label}</span>
          </label>
        ))}
      </div>
      <div className="dp-btn-row" style={{ marginTop: 14 }}>
        <button className="dp-btn dp-btn--primary" style={{ width: "100%" }} onClick={handleExport}>
          <FileDown size={13} /> Exporter en DXF
        </button>
      </div>
    </DarkPanel>
  );
};

const CalcPanel = ({ points = [], results = {}, onClose, togglePoint, setSelectedPointOnHover, isCalcLoading, calculationError }) => {
  const safePoints = Array.isArray(points) ? points : [];
  const err = results?.error || calculationError;
  const params = results?.sept_parametre;
  const erreurs = results?.erreur;

  return (
    <DarkPanel title="7 Paramètres Helmert" icon={<Sliders size={14} />} onClose={onClose} initialX={400} initialY={80} width={460}>
      {isCalcLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", color: "#818cf8", fontSize: 12 }}>
          <div style={{width:13,height:13,border:'2px solid rgba(129,140,248,0.18)',borderTopColor:'#818cf8',borderRadius:'50%',animation:'spin 0.65s linear infinite',flexShrink:0}} />
          Calcul en cours…
        </div>
      )}
      {err && <div className="dp-alert dp-alert--error"><AlertCircle size={13} />{err}</div>}

      <div className="dp-table-wrap">
        <table className="dp-table">
          <thead><tr><th>Nom</th><th>Ordre</th><th>Err X</th><th>Err Y</th><th>Err D</th><th>Inclus</th></tr></thead>
          <tbody>
            {safePoints.filter(p => p && (p.properties || p.nom)).map(p => {
              const pid = String(p.id);
              const pe  = results?.details_point?.[pid] || {};
              return (
                <tr key={p.id}
                  onMouseEnter={() => setSelectedPointOnHover?.(p)}
                  onMouseLeave={() => setSelectedPointOnHover?.(null)}
                >
                  <td>{p.properties?.nom ?? p.nom ?? "—"}</td>
                  <td>{p.ordre ?? "—"}</td>
                  <td>{typeof pe.ex === "number" ? pe.ex.toFixed(2) : "0.00"}</td>
                  <td>{typeof pe.ey === "number" ? pe.ey.toFixed(2) : "0.00"}</td>
                  <td>{typeof pe.dxy === "number" ? pe.dxy.toFixed(2) : "0.00"}</td>
                  <td><input type="checkbox" defaultChecked onChange={() => togglePoint?.(p)} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="dp-info">{safePoints.length} point(s) sélectionné(s) — minimum 3 requis</div>

      {safePoints.length >= 3 && !isCalcLoading && !err && params && (
        <>
          <div className="dp-sep" />
          <div className="dp-field">
            <label className="dp-label">Paramètres</label>
            <div className="dp-params">
              {[
                { k: "TX", v: params.Tx?.toFixed(2) },
                { k: "TY", v: params.Ty?.toFixed(2) },
                { k: "TZ", v: params.Tz?.toFixed(2) },
                { k: "K",  v: params.k?.toExponential(3) },
                { k: "RX", v: params.Rx?.toFixed(6) },
                { k: "RY", v: params.Ry?.toFixed(6) },
                { k: "RZ", v: params.Rz?.toFixed(6) },
              ].map(({ k, v }) => (
                <div key={k} className="dp-param">
                  <div className="dp-param-key">{k}</div>
                  <div className="dp-param-val">{v ?? "—"}</div>
                </div>
              ))}
            </div>
          </div>
          {erreurs && (
            <div className="dp-row">
              <div className="dp-metric" style={{ marginBottom: 0 }}>
                <div className="dp-metric-label">Err. moy. X</div>
                <div className="dp-metric-value" style={{ fontSize: 14, color: "#10b981" }}>{erreurs.ex?.toFixed(3) ?? "0.000"} m</div>
              </div>
              <div className="dp-metric" style={{ marginBottom: 0 }}>
                <div className="dp-metric-label">Err. moy. Y</div>
                <div className="dp-metric-value" style={{ fontSize: 14, color: "#10b981" }}>{erreurs.ey?.toFixed(3) ?? "0.000"} m</div>
              </div>
            </div>
          )}
        </>
      )}
    </DarkPanel>
  );
};

// ─────────────────────────────────────────────────────────────
// COORDINATE BAR
// ─────────────────────────────────────────────────────────────
const WS_STATUS = { IDLE: 'idle', CONNECTING: 'connecting', OPEN: 'open', ERROR: 'error', CLOSED: 'closed' };

const CoordBar = ({ mousePosition, mapSystem, onSystemChange, isLoading }) => {
  const [labordeCoords, setLabordeCoords] = useState({ x: null, y: null });
  const [wsStatus,      setWsStatus]      = useState(WS_STATUS.IDLE);

  const wsRef        = useRef(null);
  const debRef       = useRef(null);
  const retryRef     = useRef(null);
  const retryCount   = useRef(0);
  const pendingRef   = useRef(null);

  const connect = useCallback(() => {
    if (!WS_URL_BASE || WS_URL_BASE.includes('undefined')) return;
    setWsStatus(WS_STATUS.CONNECTING);
    const ws = new WebSocket(WS_URL_BASE);
    wsRef.current = ws;
    ws.onopen = () => {
      setWsStatus(WS_STATUS.OPEN);
      retryCount.current = 0;
      if (pendingRef.current) { ws.send(JSON.stringify(pendingRef.current)); pendingRef.current = null; }
    };
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.error) { setLabordeCoords({ x: null, y: null, err: d.error }); }
        else { setLabordeCoords({ x: d.laborde_x != null ? parseFloat(d.laborde_x).toFixed(3) : null, y: d.laborde_y != null ? parseFloat(d.laborde_y).toFixed(3) : null }); }
      } catch { /* ignore */ }
    };
    ws.onerror = () => { setWsStatus(WS_STATUS.ERROR); };
    ws.onclose = (evt) => {
      setWsStatus(WS_STATUS.CLOSED);
      wsRef.current = null;
      if (evt.code !== 1000 && retryCount.current < 5) {
        const delay = Math.min(1000 * 2 ** retryCount.current, 15000);
        retryCount.current += 1;
        retryRef.current = setTimeout(() => connect(), delay);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(retryRef.current);
    retryCount.current = 0;
    pendingRef.current = null;
    if (mapSystem !== "LABORDE") {
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(1000, "Système changé"); wsRef.current = null; }
      setWsStatus(WS_STATUS.IDLE);
      setLabordeCoords({ x: null, y: null });
      return;
    }
    connect();
    return () => {
      clearTimeout(retryRef.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(1000, "Démontage composant"); wsRef.current = null; }
    };
  }, [mapSystem, connect]);

  useEffect(() => {
    clearTimeout(debRef.current);
    if (mapSystem !== "LABORDE" || !mousePosition) return;
    const payload = { lat: mousePosition.lat, lon: mousePosition.lng };
    debRef.current = setTimeout(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) { wsRef.current.send(JSON.stringify(payload)); }
      else { pendingRef.current = payload; }
    }, 40);
    return () => clearTimeout(debRef.current);
  }, [mousePosition, mapSystem]);

  const coordText = useMemo(() => {
    if (!mousePosition) return "Déplacez le curseur sur la carte";
    if (mapSystem === "WGS84") return `Lat: ${mousePosition.lat.toFixed(6)}  Lng: ${mousePosition.lng.toFixed(6)}`;
    if (wsStatus === WS_STATUS.CONNECTING) return "Connexion WS…";
    if (wsStatus === WS_STATUS.ERROR)      return "WS indisponible — vérifiez Daphne";
    if (wsStatus === WS_STATUS.CLOSED)     return "WS déconnecté — reconnexion…";
    if (labordeCoords.err)                 return `Erreur : ${labordeCoords.err}`;
    if (labordeCoords.x == null)           return "En attente de coordonnées…";
    return `X : ${labordeCoords.x}  Y : ${labordeCoords.y}  m`;
  }, [mousePosition, mapSystem, wsStatus, labordeCoords]);

  const dotColor = mapSystem === "LABORDE"
    ? { [WS_STATUS.OPEN]: '#10b981', [WS_STATUS.CONNECTING]: '#f59e0b', [WS_STATUS.ERROR]: '#ef4444', [WS_STATUS.CLOSED]: '#ef4444' }[wsStatus] ?? '#818cf8'
    : '#818cf8';

  return (
    <div className="imap-coordbar">
      <div className="imap-coordbar-system">
        <span className="imap-system-label">Système</span>
        <select className="imap-system-select" value={mapSystem} onChange={onSystemChange}>
          <option value="WGS84">WGS 84</option>
          <option value="LABORDE">LABORDE</option>
        </select>
      </div>
      <div className="imap-coords">
        <span className="imap-coord-dot" style={{ background: dotColor, boxShadow: `0 0 6px ${dotColor}` }} title={mapSystem === "LABORDE" ? `WebSocket : ${wsStatus}` : undefined} />
        <span>{coordText}</span>
      </div>
      <div className="imap-coordbar-status">
        {(isLoading || wsStatus === WS_STATUS.CONNECTING) && (
          <><div className="imap-spin" /><span>{wsStatus === WS_STATUS.CONNECTING ? 'WS…' : 'Calcul…'}</span></>
        )}
        {mapSystem === "LABORDE" && wsStatus === WS_STATUS.OPEN && (
          <span style={{ fontSize: 10, color: '#10b981', fontWeight: 700, letterSpacing: '0.05em' }}>WS ●</span>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const InteractiveMap = () => {
  const navigate = useNavigate();
  const { items: cartItems, addToCart, isInCart } = useCart();
  const { themeId } = useTheme();

  // ── Map state ──
  const [activeLayer,    setActiveLayer]   = useState("dark");
  const [showLayers,     setShowLayers]    = useState(false);
  const [zoomAction,     setZoomAction]    = useState(null);
  const [resetView,      setResetView]     = useState(false);
  const [mousePosition,  setMousePosition] = useState(null);

  // ── Tool modes ──
  const [measureMode,   setMeasureMode]  = useState(false);
  const [areaMode,      setAreaMode]     = useState(false);
  const [selectMode,    setSelectMode]   = useState(false);
  const [selectingPoint,setSelectingPoint]=useState(false);

  // ── Measure ──
  const [measurementPoints,  setMeasurementPoints]  = useState([]);
  const [measureResult,      setMeasureResult]      = useState(null);
  const [measureError,       setMeasureError]       = useState(null);
  const [measureWindowVisible,setMeasureWindowVisible]=useState(false);

  // ── Area ──
  const [areaPoints,    setAreaPoints]   = useState([]);
  const [areaResult,    setAreaResult]   = useState(null);
  const [areaError,     setAreaError]    = useState(null);
  const [areaWindowVisible,setAreaWindowVisible]=useState(false);

  // ── Geo search ──
  const [showGeoWindow, setShowGeoWindow]=useState(false);
  const [isGeo,         setIsGeo]        =useState(false);
  const [selectedPoint, setSelectedPoint]=useState(null);
  const [radius,        setRadius]       =useState(5);
  const [geoPoints,     setGeoPoints]    =useState([]);

  // ── DXF export ──
  const [showDxfwindow,  setShowDxfwindow] =useState(false);
  const [showExportPanel,setShowExportPanel]=useState(false);
  const [isDxf,          setIsDxf]         =useState(false);
  const [selectedPointDxf,setSelectedPointDxf]=useState(null);
  const [sideSquare,     setSideSquare]    =useState(50);
  const [bbox,           setBbox]          =useState([[0,0],[0,0]]);

  // ── 7-param calc ──
  const [calcWindowVisible, setCalcWindowVisible]=useState(false);
  const [selectedCalcPoints,setSelectedCalcPoints]=useState([]);
  const [results,           setResults]          =useState(null);
  const [isCalcLoading,     setIsCalcLoading]    =useState(false);
  const [calcError,         setCalcError]        =useState(null);
  const [hoveredPoint,      setHoveredPoint]     =useState(null);

  // ── Global loading / error ──
  const [isLoading, setIsLoading]=useState(false);
  const [error,     setError]    =useState(null);

  // ── Coordinate system ──
  const [mapSystem, setMapSystem]=useState("WGS84");

  // ── Payment state ──
  const [currentPrice,    setCurrentPrice]    = useState(null);
  const [paywallEnabled,  setPaywallEnabled]  = useState(true); // false tant que le paiement des points n'est pas en place côté back
  const [purchasedIds,    setPurchasedIds]    = useState(new Set());
  const [pointDetails,    setPointDetails]    = useState({});
  const [payModalPoint,   setPayModalPoint]   = useState(null);
  const [showPayModal,    setShowPayModal]    = useState(false);

  // ── Sync tile layer with theme (light → osm, dark/geo → dark) ──
  useEffect(() => {
    setActiveLayer(prev => {
      if (prev === 'satellite') return prev;
      return themeId === 'light' ? 'osm' : 'dark';
    });
  }, [themeId]);

  // ── Load price and purchased points on mount ──
  useEffect(() => {
    paymentsApi.price()
      .then(r => {
        setCurrentPrice(r.data);
        setPaywallEnabled(r.data?.paywall_enabled ?? true);
      })
      .catch(err => {
        // Le prix peut échouer pour une raison indépendante (ex: aucun
        // PointPrice actif en base, 400) sans que ça remette en cause le
        // flag paywall_enabled, renvoyé même dans la réponse d'erreur.
        const flag = err.response?.data?.paywall_enabled;
        if (flag !== undefined) setPaywallEnabled(flag);
      });
    paymentsApi.myPoints()
      .then(r => {
        const ids = new Set(r.data.map(p => p.point));
        setPurchasedIds(ids);
      })
      .catch(() => {});
  }, []);

  // ── Fetch point detail (Laborde + PDF) after purchase ──
  const fetchPointDetail = useCallback(async (pointId) => {
    if (pointDetails[pointId]) return;
    try {
      const r = await paymentsApi.pointDetail(pointId);
      setPointDetails(prev => ({ ...prev, [pointId]: r.data }));
    } catch { /* accès refusé */ }
  }, [pointDetails]);

  // ── Called after successful payment ──
  const handlePaymentSuccess = useCallback((pointId) => {
    setPurchasedIds(prev => new Set([...prev, pointId]));
    setShowPayModal(false);
    fetchPointDetail(pointId);
  }, [fetchPointDetail]);

  const handleZoomComplete = useCallback(() => setZoomAction(null), []);
  const handleResetDone    = useCallback(() => setResetView(false), []);
  const selectedCalcPointIds = selectedCalcPoints.map(p => String(p.id));

  // ── Mode management ──
  const clearMeasure = () => { setMeasurementPoints([]); setMeasureResult(null); setMeasureError(null); setMeasureWindowVisible(false); };
  const clearArea    = () => { setAreaPoints([]);         setAreaResult(null);   setAreaError(null);    setAreaWindowVisible(false); };
  const clearAllModes = () => { setMeasureMode(false); clearMeasure(); setAreaMode(false); clearArea(); };

  const handleToggleSelectMode  = () => { clearAllModes(); setSelectMode(p => !p); };
  const handleToggleMeasureMode = () => { clearAllModes(); setSelectMode(false); setMeasureMode(p => !p); };
  const handleToggleAreaMode    = () => {
    if (mapSystem === "WGS84" && !areaMode) return;
    clearAllModes(); setSelectMode(false); setAreaMode(p => !p);
  };

  const handleSystemChange = (e) => {
    setMapSystem(e.target.value);
    setSelectMode(false); clearAllModes();
    setGeoPoints([]); setSelectedCalcPoints([]); setResults(null);
  };

  // ── Geo search ──
  const handleSubmit = async () => {
    if (!selectedPoint || !radius) return;
    setError(null); setIsLoading(true);
    try {
      const res = await fetchWithAuth(`${URLS_BASE}/geodesie/points-geodesiques/?lat=${selectedPoint.lat}&lon=${selectedPoint.lng}&rayon=${radius}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setGeoPoints(data.features || []);
      setSelectedCalcPoints([]); setResults(null);
    } catch (e) { setError("Erreur récupération : " + e.message); setGeoPoints([]); }
    finally { setIsLoading(false); }
  };

  // ── Map click handlers ──
  const handleMapClick = (latlng) => {
    if (selectingPoint && isGeo) { setSelectedPoint(latlng); setSelectingPoint(false); setShowGeoWindow(true); setIsDxf(false); }
  };

  const handleMapClickOnDxf = async (latlng) => {
    if (selectingPoint && isDxf) {
      setSelectedPointDxf(latlng); setSelectingPoint(false); setShowDxfwindow(true); setIsGeo(false);
      try {
        const res = await fetchWithAuth(`${URLS_BASE}/geodesie/getBbox/`, {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ center_lat: latlng.lat, center_lng: latlng.lng, side: sideSquare }),
        });
        if (!res.ok) throw new Error("Erreur serveur");
        const result = await res.json();
        setBbox([[result.min_lat, result.min_lng], [result.max_lat, result.max_lng]]);
      } catch (err) { console.error(err); }
    }
  };

  // ── DXF export ──
  const handleExport = async (payload) => {
    try {
      const res = await fetchWithAuth(`${URLS_BASE}/draw/dxf/`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url  = window.URL.createObjectURL(blob);
        const a    = Object.assign(document.createElement("a"), { href: url, download: "madjeo_export.dxf" });
        document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); document.body.removeChild(a);
        setShowExportPanel(false);
      } else {
        const d = await res.json();
        alert("Erreur export : " + d.error);
      }
    } catch { alert("Erreur réseau lors de l'export."); }
  };

  // ── 7-param calc ──
  const triggerCalculation = useCallback((pointIds) => {
    setCalcError(null);
    if (pointIds.length < 3) { setResults(null); return; }
    setIsCalcLoading(true); setCalcWindowVisible(true);
    fetchWithAuth(`${URLS_BASE}/laborde/7parametres/`, {
      method: "POST",
      headers: getAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ point_ids: pointIds }),
    })
      .then(res => { if (!res.ok) throw new Error(`Erreur ${res.status}`); return res.json(); })
      .then(data => { if (data?.error) { setCalcError(data.error); setResults(null); } else setResults(data); })
      .catch(err => { setCalcError("Erreur : " + err.message); setResults(null); })
      .finally(() => setIsCalcLoading(false));
  }, []);

  const handleSelectCalcPoint = (pt) => {
    if (!selectMode) return;
    setSelectedCalcPoints(prev => {
      const updated = prev.find(p => p.id === pt.id)
        ? prev.filter(p => p.id !== pt.id)
        : [...prev, { ...pt, selected: true }];
      triggerCalculation(updated.map(p => p.id));
      if (updated.length > 0) setCalcWindowVisible(true);
      return updated;
    });
  };

  const togglePoint = (pt) => {
    if (!pt?.id) return;
    setSelectedCalcPoints(prev => {
      const updated = prev.filter(p => p.id !== pt.id);
      triggerCalculation(updated.map(p => p.id));
      setCalcWindowVisible(updated.length > 0);
      return updated;
    });
  };

  // ── Icon factory : cercle coloré selon has_pdf / purchased ──
  // Couleur basée sur l'achat réel (pas le bypass paywall temporaire), sinon
  // tous les points passeraient en indigo "Acheté" tant que le paywall est
  // désactivé et la distinction "avec/sans fiche" (vert/orange) disparaîtrait.
  const getPointIcon = useCallback((pt, isSelected, isHovered) => {
    const hasPdf         = pt.properties?.has_pdf ?? false;
    const reallyPurchased = purchasedIds.has(pt.id);
    return createGeoCircleIcon(hasPdf, reallyPurchased, isSelected, isHovered);
  }, [purchasedIds]);

  // ── Active mode badge ──
  const activeBadge = useMemo(() => {
    if (selectingPoint) return { cls: "pick",    text: "Cliquez sur la carte pour sélectionner un point" };
    if (measureMode)    return { cls: "measure", text: `Mode Mesure  (${mapSystem}) — cliquez pour ajouter des points` };
    if (areaMode)       return { cls: "area",    text: `Mode Surface (${mapSystem}) — cliquez pour ajouter des sommets` };
    if (selectMode)     return { cls: "select",  text: "Mode Sélection — cliquez sur les points géodésiques" };
    return null;
  }, [selectingPoint, measureMode, areaMode, selectMode, mapSystem]);

  const mapCursor = selectingPoint || selectMode || measureMode || areaMode ? "crosshair" : "grab";

  return (
    <div className="imap">

      {/* ── Carte ── */}
      <MapContainer
        center={[-18.91552, 47.52166]}
        zoom={15} minZoom={6} maxZoom={22}
        maxBounds={[[-26.6278, 30.1245], [-11.3508, 66.9506]]}
        maxBoundsViscosity={1.0}
        style={{ height: "100%", width: "100%", cursor: mapCursor }}
        zoomControl={false}
      >
        <TileLayer
          key={activeLayer}
          url={TILE_LAYERS[activeLayer].url}
          attribution={TILE_LAYERS[activeLayer].attribution}
          maxZoom={22}
          maxNativeZoom={TILE_LAYERS[activeLayer].maxNative}
          className={TILE_LAYERS[activeLayer].className || ""}
        />

        <MapZoomController zoomAction={zoomAction} onZoomComplete={handleZoomComplete} />
        <MapResetView trigger={resetView} onDone={handleResetDone} />
        <MapMover setMousePosition={setMousePosition} />
        <ScaleControl />

        <MapMeasureController
          measureMode={measureMode}
          measurementPoints={measurementPoints}
          setMeasurementPoints={setMeasurementPoints}
          mapSystem={mapSystem}
          setIsLoading={setIsLoading}
          setMeasureResult={setMeasureResult}
          setMeasureError={setMeasureError}
          setMeasureWindowVisible={setMeasureWindowVisible}
        />
        <MapAreaController
          areaMode={areaMode}
          areaPoints={areaPoints}
          setAreaPoints={setAreaPoints}
          mapSystem={mapSystem}
          setAreaResult={setAreaResult}
          setAreaError={setAreaError}
          setGlobalLoading={setIsLoading}
          setAreaWindowVisible={setAreaWindowVisible}
        />

        {selectingPoint && isGeo  && <MapClickHandler onSelect={handleMapClick} />}
        {selectingPoint && isDxf  && <MapClickHandler onSelect={handleMapClickOnDxf} />}

        {selectedPoint && isGeo && (
          <>
            <Marker position={selectedPoint} icon={markerIcon} />
            <Circle center={selectedPoint} radius={radius * 1000}
              pathOptions={{ color: "#818cf8", fillColor: "#818cf8", fillOpacity: 0.07, weight: 1.5, dashArray: "4 8" }} />
          </>
        )}

        {isDxf && bbox && (
          <Rectangle bounds={bbox}
            pathOptions={{ color: "#06b6d4", weight: 1.5, fillColor: "#06b6d4", fillOpacity: 0.07, dashArray: "4 8" }} />
        )}

        {geoPoints.map((pt) => {
          if (!pt?.geometry?.coordinates) return null;
          const [lng, lat] = pt.geometry.coordinates;
          const isSelected  = selectedCalcPointIds.includes(String(pt.id));
          const isHovered   = hoveredPoint?.id === pt.id;
          const reallyPurchased = purchasedIds.has(pt.id);
          const canAccess   = !paywallEnabled || reallyPurchased;
          const hasPdf      = pt.properties?.has_pdf ?? false;
          const detail      = pointDetails[pt.id];
          const markerKey   = `geo-${pt.id}-${isSelected ? 's' : 'n'}-${canAccess ? 'p' : 'u'}`;

          return (
            <Marker
              key={markerKey}
              position={[lat, lng]}
              icon={getPointIcon(pt, isSelected, isHovered)}
              opacity={selectMode ? (isSelected ? 1 : 0.6) : 1}
              eventHandlers={{
                click: () => {
                  handleSelectCalcPoint(pt);
                  if (canAccess && !detail) fetchPointDetail(pt.id);
                },
                mouseover: () => setHoveredPoint(pt),
                mouseout:  () => setHoveredPoint(null),
              }}
            >
              <Popup>
                <div className="dpopup">
                  <h6 style={{ marginBottom: 6, color: reallyPurchased ? '#818cf8' : hasPdf ? '#22c55e' : '#f97316' }}>
                    {pt.properties?.nom ?? "Point géodésique"}
                  </h6>

                  {/* Infos de base — toujours libres */}
                  <div className="dpopup-row"><span className="dpopup-key">Ordre</span><span className="dpopup-val">{pt.properties?.ordre ?? "—"}</span></div>
                  <div className="dpopup-row"><span className="dpopup-key">Précision</span><span className="dpopup-val">{pt.properties?.precision ?? "—"}</span></div>
                  <div className="dpopup-row"><span className="dpopup-key">Lat (WGS84)</span><span className="dpopup-val">{lat.toFixed(6)}</span></div>
                  <div className="dpopup-row"><span className="dpopup-key">Lng (WGS84)</span><span className="dpopup-val">{lng.toFixed(6)}</span></div>

                  <div style={{ margin: '8px 0', borderTop: '1px solid #1e293b' }} />

                  {/* Section Laborde + PDF — verrouillée ou déverrouillée */}
                  {canAccess && detail ? (
                    <>
                      <div className="dpopup-row">
                        <span className="dpopup-key">Laborde X</span>
                        <span className="dpopup-val" style={{ color: '#818cf8' }}>{detail.laborde_x ?? "—"}</span>
                      </div>
                      <div className="dpopup-row">
                        <span className="dpopup-key">Laborde Y</span>
                        <span className="dpopup-val" style={{ color: '#818cf8' }}>{detail.laborde_y ?? "—"}</span>
                      </div>
                      {detail.pdf_url && (
                        <a href={detail.pdf_url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8,
                            color: '#22c55e', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                          <FileDown size={13} /> Télécharger la fiche PDF
                        </a>
                      )}
                    </>
                  ) : canAccess && !detail ? (
                    <div style={{ color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="dp-spin" style={{width:10,height:10,border:'1.5px solid #334155',borderTopColor:'#818cf8',borderRadius:'50%',animation:'spin 0.65s linear infinite'}} />
                      Chargement…
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, marginBottom: 10 }}>
                        <Lock size={12} />
                        <span>Coordonnées Laborde {hasPdf ? '+ PDF' : ''} — accès payant</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {/* Acheter directement */}
                        <button
                          className="dp-btn dp-btn--primary"
                          style={{ width: '100%', gap: 6, opacity: currentPrice ? 1 : 0.5 }}
                          disabled={!currentPrice}
                          onClick={(e) => { e.stopPropagation(); if (currentPrice) { setPayModalPoint(pt); setShowPayModal(true); } }}
                        >
                          <ShoppingCart size={13} />
                          {currentPrice ? `Acheter — ${currentPrice.amount_mga} MGA` : 'Chargement du prix…'}
                        </button>
                        {/* Ajouter au panier */}
                        {isInCart(pt.id) ? (
                          <button
                            className="dp-btn"
                            style={{ width: '100%', gap: 6, background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.3)', color: '#818cf8' }}
                            onClick={(e) => { e.stopPropagation(); navigate('/expert/panier'); }}
                          >
                            <ShoppingCart size={13} /> Voir le panier
                          </button>
                        ) : (
                          <button
                            className="dp-btn"
                            style={{ width: '100%', gap: 6, background: '#1e293b', border: '1px solid #334155', color: '#94a3b8' }}
                            onClick={(e) => { e.stopPropagation(); addToCart(pt, currentPrice); }}
                          >
                            <ShoppingCart size={13} /> Ajouter au panier
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* ── Mode badge ── */}
      {activeBadge && (
        <div className={`imap-badge imap-badge--${activeBadge.cls}`}>
          <span>{activeBadge.text}</span>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="imap-toolbar">
        <button className={`imap-tool-btn${showGeoWindow ? " imap-tool-btn--active" : ""}`}
          data-tip="Points géodésiques"
          onClick={() => { setShowGeoWindow(v => !v); setIsGeo(true); setIsDxf(false); }}>
          <Crosshair size={18} />
        </button>

        <button className={`imap-tool-btn${measureMode ? " imap-tool-btn--active-cyan" : ""}`}
          data-tip="Mesure distance"
          onClick={handleToggleMeasureMode}>
          <Ruler size={18} />
        </button>

        <button
          className={`imap-tool-btn${areaMode ? " imap-tool-btn--active" : ""}`}
          data-tip={mapSystem === "WGS84" ? "Surface — mode LABORDE requis" : "Mesure surface"}
          style={mapSystem === "WGS84" ? { opacity: 0.35, cursor: "not-allowed" } : {}}
          onClick={mapSystem !== "WGS84" ? handleToggleAreaMode : undefined}>
          <Hexagon size={18} />
        </button>

        <div className="imap-tool-sep" />

        <button className={`imap-tool-btn${selectMode ? " imap-tool-btn--active-amber" : ""}`}
          data-tip="Calcul 7 paramètres"
          onClick={handleToggleSelectMode}>
          <Sliders size={18} />
        </button>

        <button className={`imap-tool-btn${showDxfwindow ? " imap-tool-btn--active" : ""}`}
          data-tip="Export DXF"
          onClick={() => { setShowDxfwindow(v => !v); setIsDxf(true); setIsGeo(false); }}>
          <FileDown size={18} />
        </button>

        <div className="imap-tool-sep" />

        <button className={`imap-tool-btn${showLayers ? " imap-tool-btn--active" : ""}`}
          data-tip="Fond de carte"
          onClick={() => setShowLayers(v => !v)}>
          <Layers size={18} />
        </button>

        <div className="imap-tool-sep" />

        {/* Panier avec badge */}
        <button
          className="imap-tool-btn"
          data-tip={`Panier (${cartItems.length})`}
          style={{ position: 'relative' }}
          onClick={() => navigate('/expert/panier')}
        >
          <ShoppingCart size={18} />
          {cartItems.length > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 4,
              background: '#4f46e5', color: '#fff',
              borderRadius: '50%', width: 14, height: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, lineHeight: 1,
            }}>{cartItems.length > 9 ? '9+' : cartItems.length}</span>
          )}
        </button>
      </div>

      {/* ── Layer switcher ── */}
      {showLayers && (
        <div className="imap-layers-panel" style={{ top: "50%", transform: "translateY(-50%)" }}>
          {Object.entries(TILE_LAYERS).map(([key, cfg]) => (
            <div key={key}
              className={`imap-layer-opt${activeLayer === key ? " imap-layer-opt--active" : ""}`}
              onClick={() => { setActiveLayer(key); setShowLayers(false); }}>
              <span className="imap-layer-dot" style={{ background: cfg.color }} />
              {cfg.label}
              {activeLayer === key && <Check size={12} style={{ marginLeft: "auto" }} />}
            </div>
          ))}
        </div>
      )}

      {/* ── Zoom controls ── */}
      <div className="imap-zoom">
        <button className="imap-zoom-btn" title="Zoom avant"  onClick={() => setZoomAction("IN")}><Plus size={16} /></button>
        <div className="imap-zoom-sep" />
        <button className="imap-zoom-btn" title="Zoom arrière" onClick={() => setZoomAction("OUT")}><Minus size={16} /></button>
        <div className="imap-zoom-sep" />
        <button className="imap-zoom-btn" title="Réinitialiser la vue" onClick={() => setResetView(true)}><Home size={15} /></button>
      </div>

      {/* ── Coordinate bar ── */}
      <CoordBar
        mousePosition={mousePosition}
        mapSystem={mapSystem}
        onSystemChange={handleSystemChange}
        isLoading={isLoading || isCalcLoading}
      />

      {/* ══════════════════════ FLOATING PANELS ══════════════════════ */}

      {showGeoWindow && (
        <GeoSearchPanel
          selectedPoint={selectedPoint}
          setSelectedPoint={setSelectedPoint}
          radius={radius}
          setRadius={setRadius}
          onSubmit={handleSubmit}
          onPickClick={() => { setSelectingPoint(true); setShowGeoWindow(false); }}
          onClose={() => { setShowGeoWindow(false); setIsGeo(false); }}
          isLoading={isLoading}
          error={error}
        />
      )}

      {showDxfwindow && (
        <DxfZonePanel
          selectedPointDxf={selectedPointDxf}
          setSelectedPointDxf={setSelectedPointDxf}
          sideSquare={sideSquare}
          setSideSquare={setSideSquare}
          onSubmit={() => setShowExportPanel(true)}
          onPickClick={() => { setSelectingPoint(true); setShowDxfwindow(false); }}
          onClose={() => { setShowDxfwindow(false); setShowExportPanel(false); setIsDxf(false); }}
          isLoading={isLoading}
          error={error}
        />
      )}

      {showExportPanel && (
        <DxfConfigPanel
          onClose={() => setShowExportPanel(false)}
          bbox={bbox}
          onExport={handleExport}
        />
      )}

      {calcWindowVisible && (
        <CalcPanel
          points={selectedCalcPoints}
          results={results}
          onClose={() => setCalcWindowVisible(false)}
          togglePoint={togglePoint}
          setSelectedPointOnHover={setHoveredPoint}
          isCalcLoading={isCalcLoading}
          calculationError={calcError}
        />
      )}

      {measureWindowVisible && (
        <MeasurePanel
          onClose={() => setMeasureWindowVisible(false)}
          totalDistanceM={measureResult}
          mapSystem={mapSystem}
          isLoading={isLoading}
          error={measureError}
          onClear={clearMeasure}
          numPoints={measurementPoints.length}
        />
      )}

      {areaWindowVisible && (
        <AreaPanel
          onClose={() => setAreaWindowVisible(false)}
          areaM2={areaResult}
          mapSystem={mapSystem}
          isLoading={isLoading}
          error={areaError}
          onClear={clearArea}
          numPoints={areaPoints.length}
        />
      )}

      {/* ── Payment modal ── */}
      {showPayModal && payModalPoint && (
        <PaymentModal
          point={payModalPoint}
          price={currentPrice}
          onClose={() => { setShowPayModal(false); setPayModalPoint(null); }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default InteractiveMap;
